import { useState, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Fuel, Upload, AlertTriangle, RefreshCw, Car, ListChecks, Users } from 'lucide-react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { linkPvNou } from './flota/flotaComun'
import { DateRangeField } from '../../components/ui/DateField'
import { AntetFix } from '../../components/ui/AntetFix'

// ---- Tipuri (oglindesc raspunsurile API) ----
interface VehicleReportRow {
  vehicleId: number
  plate: string
  driverName: string | null
  vatRegime: string
  normedL100: number | null
  fuelCost: number
  dryCost: number
  totalCost: number
  liters: number
  txCount: number
  kmDriven: number
  avgL100: number | null
  deviationPct: number | null
  costPerKm: number | null
  months: { ym: string; cost: number; liters: number }[]
  /** Cine a condus mașina în perioadă, după procesele-verbale. `userId` gol = firma. */
  soferi?: { userId: number | null; nume: string; cost: number; litri: number; nrTx: number }[]
  /** Alimentările fără PV pe perioada lor. */
  nealocat?: { cost: number; nrTx: number; de: string | null }
  calitateKm?: {
    alimentari: number
    cuKmPlauzibil: number
    segmenteValide: number
    segmenteSarite: number
    faraKm: boolean
  }
}
interface Anomaly {
  vehicleId: number
  plate: string
  type: string
  detail: string
  de?: string
}
interface TxRow {
  id: number
  kind: 'FUEL' | 'DRY'
  txAt: string
  productName: string | null
  quantity: string
  unit: string
  amount: string
  odometerKm: number | null
  invoiceNo: string | null
  vehicleId: number
  vehicle: { plate: string }
  /** Cine avea mașina la alimentare, după PV-uri. */
  atribuire?: {
    fel: 'sofer' | 'firma' | 'nealocat'
    userId?: number
    motiv?: 'fara_pv' | 'neconcordant'
    nume: string | null
  }
}
interface ImportRow {
  id: number
  source: string
  reportType: string
  fileName: string
  status: string
  rowsTotal: number
  rowsInserted: number
  rowsDuplicate: number
  rowsError: number
  createdAt: string
}

const ron = new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' })
const num = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 })
const fmtRon = (v: number | null): string => (v == null ? '—' : ron.format(v))
const fmtNum = (v: number | null): string => (v == null ? '—' : num.format(v))
const fmtDate = (iso: string): string => new Date(iso).toLocaleString('ro-RO')

type Tab = 'dashboard' | 'tranzactii' | 'masini' | 'import'

interface VehicleFull {
  id: number
  plate: string
  brand: string | null
  model: string | null
  chassis: string | null
  driverName: string | null
  normedL100: string | null
  vatRegime: 'v50' | 'v100'
  active: boolean
  /** Ultima zi valabilă a actului (coloană DATE). Alertă cu 30 de zile înainte. */
  itpExpira?: string | null
  rcaExpira?: string | null
  rovinietaExpira?: string | null
}

export function RompetrolPage() {
  const [tab, setTab] = useState<Tab>('dashboard')

  return (
    <div className="space-y-4">
      <AntetFix>
        <div className="flex items-center gap-2">
          <Fuel className="h-6 w-6 text-orange-500" />
          <h1 className="text-2xl font-bold">Flotă Rompetrol</h1>
          <span className="ml-2 rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
            Fill&amp;Go
          </span>
          <LinkPv />
        </div>

        <div className="flex gap-1 border-b border-slate-200">
          {(
            [
              ['dashboard', 'Dashboard per mașină', ListChecks],
              ['tranzactii', 'Tranzacții', Car],
              ['masini', 'Mașini', Users],
              ['import', 'Import / Sincronizare', Upload],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
                tab === key
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </AntetFix>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'tranzactii' && <TranzactiiTab />}
      {tab === 'masini' && <MasiniTab />}
      {tab === 'import' && <ImportTab />}
    </div>
  )
}

/** Linkul spre procesele-verbale, pentru cine le vede. */
function LinkPv() {
  const vede = useAuthStore((s) => s.permissions.includes('operational.flota_pv:read'))
  if (!vede) return null
  return (
    <Link
      to="/operational/flota/procese-verbale"
      className="ml-auto rounded-lg border border-orange-300 px-3 py-1 text-sm font-medium text-orange-700 hover:bg-orange-50"
    >
      Procese-verbale de predare
    </Link>
  )
}

/** Numărul mașinii, cu link spre cronologia ei (cine a avut-o), pentru cine vede PV-urile. */
function Placuta({ vehicleId, plate }: { vehicleId: number; plate: string }) {
  const vede = useAuthStore((s) => s.permissions.includes('operational.flota_pv:read'))
  if (!vede) return <>{plate}</>
  return (
    <Link to={`/operational/flota/masini/${vehicleId}`} className="hover:underline">
      {plate}
    </Link>
  )
}

/** Filtrul pe șofer: id-ul omului din PV, `firma` sau `nealocat`. */
type FiltruSofer = '' | 'firma' | 'nealocat' | `${number}`

type SortKey =
  | 'plate'
  | 'totalCost'
  | 'liters'
  | 'kmDriven'
  | 'avgL100'
  | 'deviationPct'
  | 'costPerKm'

const pad2 = (n: number): string => String(n).padStart(2, '0')
const fmtLocal = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

/**
 * Butoane de perioadă + De la / Până la, la fel ca pe pagina Sell-out.
 *
 * Anii sunt cei REALI din alimentări, nu „anul curent / an precedent": pe un an fără
 * date, butonul ar filtra pe gol și ar părea că s-au pierdut datele. Trimestrele stau
 * într-o listă, nu ca butoane — cu doi ani ar fi opt butoane în plus.
 */
function PeriodFilter({
  from,
  to,
  setFrom,
  setTo,
}: {
  from: string
  to: string
  setFrom: (v: string) => void
  setTo: (v: string) => void
}) {
  const yearsQuery = useQuery({
    queryKey: ['rompetrol', 'years'],
    queryFn: async () =>
      (await api.get<{ years: string[] }>('/rompetrol/reports/years')).data.years,
    staleTime: 60 * 60 * 1000,
  })
  const years = yearsQuery.data ?? []

  const presets = useMemo(() => {
    const now = new Date()
    const y = now.getFullYear()
    const luna = (an: number, m: number): [string, string] => [
      fmtLocal(new Date(an, m, 1)),
      fmtLocal(new Date(an, m + 1, 0)),
    ]
    const prev = new Date(y, now.getMonth() - 1, 1)
    const list: { key: string; label: string; from: string; to: string }[] = [
      { key: 'tot', label: 'Tot', from: '', to: '' },
      { key: 'lunaCurenta', label: 'Luna curentă', ...zip(luna(y, now.getMonth())) },
      {
        key: 'lunaPrecedenta',
        label: 'Luna precedentă',
        ...zip(luna(prev.getFullYear(), prev.getMonth())),
      },
    ]
    for (const yr of years)
      list.push({
        key: `an-${yr}`,
        label: yr,
        from: `${yr}-01-01`,
        to: `${yr}-12-31`,
      })
    return list
  }, [years])

  const quarters = useMemo(
    () =>
      years.flatMap((yr) =>
        [1, 2, 3, 4].map((q) => ({
          key: `${yr}-Q${q}`,
          label: `Q${q} ${yr}`,
          from: fmtLocal(new Date(Number(yr), q * 3 - 3, 1)),
          to: fmtLocal(new Date(Number(yr), q * 3, 0)),
        })),
      ),
    [years],
  )

  const activeKey =
    presets.find((p) => p.from === from && p.to === to)?.key ??
    quarters.find((p) => p.from === from && p.to === to)?.key ??
    ''

  const aplica = (p: { from: string; to: string }): void => {
    setFrom(p.from)
    setTo(p.to)
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {presets.map((p) => (
          <button
            key={p.key}
            onClick={() => aplica(p)}
            className={`rounded-md border px-3 py-1.5 text-sm font-semibold shadow-sm transition ${
              activeKey === p.key
                ? 'border-b4l-blue bg-b4l-blue text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:border-b4l-blue hover:text-b4l-blue-dark'
            }`}
          >
            {p.label}
          </button>
        ))}
        {quarters.length > 0 && (
          <select
            value={quarters.some((q) => q.key === activeKey) ? activeKey : ''}
            onChange={(e) => {
              const q = quarters.find((x) => x.key === e.target.value)
              if (q) aplica(q)
            }}
            className={`rounded-md border px-3 py-1.5 text-sm font-semibold shadow-sm ${
              quarters.some((q) => q.key === activeKey)
                ? 'border-b4l-blue bg-b4l-blue text-white'
                : 'border-slate-200 bg-white text-slate-700'
            }`}
          >
            <option value="">Trimestru…</option>
            {quarters.map((q) => (
              <option key={q.key} value={q.key}>
                {q.label}
              </option>
            ))}
          </select>
        )}
      </div>
      <DateRangeField
        fromValue={from}
        toValue={to}
        onChange={(f, t) => {
          setFrom(f)
          setTo(t)
        }}
      />
    </>
  )
}

/** `[de la, până la]` -> `{ from, to }`, ca preseturile să se scrie pe un rând. */
function zip([from, to]: [string, string]): { from: string; to: string } {
  return { from, to }
}

function DashboardTab() {
  // Implicit anul curent: „Tot" amesteca trei ani si face cifrele necomparabile cu
  // orice altceva din firma, care se raporteaza pe an.
  const anCurent = new Date().getFullYear()
  const [from, setFrom] = useState(`${anCurent}-01-01`)
  const [to, setTo] = useState(`${anCurent}-12-31`)
  const [plate, setPlate] = useState('')
  /*
    Soferul vine din procesele-verbale (task #2445), nu din numele trecut pe masina: filtrat pe
    un om, raportul arata doar alimentarile facute cat a avut el fiecare masina. Inainte, filtrul
    arata masinile al caror sofer ERA el azi, cu tot istoricul lor.
  */
  const [driver, setDriver] = useState<FiltruSofer>('')
  const [kind, setKind] = useState<'' | 'FUEL' | 'DRY'>('')
  const [sortKey, setSortKey] = useState<SortKey>('totalCost')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const poateScriePv = useAuthStore((s) => s.permissions.includes('operational.flota_pv:write'))

  const params = {
    from: from || undefined,
    to: to ? `${to}T23:59:59` : undefined,
    kind: kind || undefined,
  }
  // Raportul intreg da si lista de soferi pentru filtru.
  const reportToate = useQuery({
    queryKey: ['rompetrol', 'report', from, to, kind],
    queryFn: async () =>
      (await api.get<{ rows: VehicleReportRow[] }>('/rompetrol/reports/per-vehicle', { params }))
        .data.rows,
  })
  const reportSofer = useQuery({
    queryKey: ['rompetrol', 'report', from, to, kind, driver],
    enabled: driver !== '',
    queryFn: async () =>
      (
        await api.get<{ rows: VehicleReportRow[] }>('/rompetrol/reports/per-vehicle', {
          params: { ...params, sofer: driver },
        })
      ).data.rows,
  })
  const report = driver ? reportSofer : reportToate
  const alerts = useQuery({
    queryKey: ['rompetrol', 'alerts', from, to, kind, driver],
    queryFn: async () =>
      (
        await api.get<{ rows: Anomaly[] }>('/rompetrol/alerts', {
          params: { ...params, sofer: driver || undefined },
        })
      ).data.rows,
  })

  const rows = useMemo(() => report.data ?? [], [report.data])
  const drivers = useMemo(() => {
    const m = new Map<number, string>()
    for (const r of reportToate.data ?? [])
      for (const s of r.soferi ?? []) if (s.userId != null) m.set(s.userId, s.nume)
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], 'ro'))
  }, [reportToate.data])
  const areNealocat = (reportToate.data ?? []).some((r) => (r.nealocat?.nrTx ?? 0) > 0)
  const areFirma = (reportToate.data ?? []).some((r) => r.soferi?.some((s) => s.userId == null))

  const filtered = useMemo(() => {
    let r = rows
    if (plate) r = r.filter((x) => x.plate === plate)
    const dir = sortDir === 'asc' ? 1 : -1
    return [...r].sort((a, b) => {
      const va = a[sortKey]
      const vb = b[sortKey]
      if (va == null && vb == null) return 0
      if (va == null) return 1 // null mereu la coadă
      if (vb == null) return -1
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir
      return ((va as number) - (vb as number)) * dir
    })
  }, [rows, plate, sortKey, sortDir])

  // Date grafice: litri + cost carburant pe luna (agregat pe randurile filtrate)
  const monthly = useMemo(() => {
    const m = new Map<string, { ym: string; liters: number; cost: number }>()
    for (const r of filtered) {
      for (const b of r.months) {
        const e = m.get(b.ym) ?? { ym: b.ym, liters: 0, cost: 0 }
        e.liters += b.liters
        e.cost += b.cost
        m.set(b.ym, e)
      }
    }
    return [...m.values()]
      .sort((a, b) => a.ym.localeCompare(b.ym))
      .map((x) => ({
        ym: x.ym,
        liters: Math.round(x.liters * 100) / 100,
        cost: Math.round(x.cost),
      }))
  }, [filtered])

  // Consum L/100km per masina (doar cele cu valoare)
  const consumByVehicle = useMemo(
    () =>
      filtered
        .filter((r) => r.avgL100 != null)
        .map((r) => ({
          plate: r.plate,
          driver: r.driverName,
          l100: r.avgL100 as number,
          peste: r.deviationPct != null && r.deviationPct > 20,
        }))
        .sort((a, b) => b.l100 - a.l100),
    [filtered],
  )

  // Litri carburant per masina
  const litersByVehicle = useMemo(
    () =>
      filtered
        .filter((r) => r.liters > 0)
        .map((r) => ({ plate: r.plate, driver: r.driverName, liters: r.liters }))
        .sort((a, b) => b.liters - a.liters),
    [filtered],
  )

  // Mapare nr. inmatriculare -> soferii din PV-uri (pentru tooltip-ul graficelor)
  const driverByPlate = useMemo(() => {
    const m: Record<string, string> = {}
    for (const r of filtered) {
      const n = (r.soferi ?? []).map((s) => s.nume).join(', ')
      if (n) m[r.plate] = n
    }
    return m
  }, [filtered])
  const plateLabel = (p: string): string => (driverByPlate[p] ? `${p} — ${driverByPlate[p]}` : p)

  const toggleSort = (k: SortKey): void => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(k)
      setSortDir(k === 'plate' ? 'asc' : 'desc')
    }
  }
  const SortTh = ({ k, label, right }: { k: SortKey; label: string; right?: boolean }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`cursor-pointer select-none px-3 py-2 hover:text-slate-700 ${right ? 'text-right' : ''}`}
    >
      {label}
      {sortKey === k && <span className="ml-0.5">{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  )

  return (
    <div className="space-y-4">
      {/* Filtre */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <PeriodFilter from={from} to={to} setFrom={setFrom} setTo={setTo} />
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase text-slate-400">Tip</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as '' | 'FUEL' | 'DRY')}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">Toate</option>
            <option value="FUEL">Carburant</option>
            <option value="DRY">DRY</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase text-slate-400">Mașină</label>
          <select
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">Toate</option>
            {rows.map((r) => (
              <option key={r.vehicleId} value={r.plate}>
                {r.plate}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase text-slate-400">Șofer (după PV)</label>
          <select
            value={driver}
            onChange={(e) => setDriver(e.target.value as FiltruSofer)}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
            disabled={drivers.length === 0 && !areNealocat}
          >
            <option value="">Toți</option>
            {drivers.map(([id, nume]) => (
              <option key={id} value={id}>
                {nume}
              </option>
            ))}
            {areFirma && <option value="firma">Firma (la sediu)</option>}
            {areNealocat && <option value="nealocat">Nealocat / de completat</option>}
          </select>
        </div>
      </div>

      {report.isLoading ? (
        <div className="text-slate-500">Se încarcă…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-500">
          Nicio tranzacție în perioada selectată. Încarcă un raport în tab-ul „Import” sau lărgește
          perioada.
        </div>
      ) : (
        <>
          {alerts.data && alerts.data.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="mb-2 flex items-center gap-2 font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" /> {alerts.data.length} alerte
              </div>
              <ul className="space-y-1 text-sm text-amber-700">
                {alerts.data
                  .filter((a) => !plate || a.plate === plate)
                  .map((a, i) => (
                    <li key={i}>
                      <span className="font-mono font-medium">{a.plate}</span> — {a.detail}
                      {a.type === 'fara_pv' && poateScriePv && (
                        <>
                          {' '}
                          <Link
                            to={`/operational/flota/masini/${a.vehicleId}`}
                            className="font-medium text-orange-700 underline"
                          >
                            Completează PV-urile
                          </Link>
                        </>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/* Grafice consum combustibil */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-3 text-sm font-medium text-slate-700">Litri carburant pe lună</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="ym" style={{ fontSize: 11 }} />
                  <YAxis style={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v) => [`${fmtNum(Number(v))} L`, 'Litri']}
                    labelStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="liters" fill="#f97316" radius={[4, 4, 0, 0]} maxBarSize={56} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-3 text-sm font-medium text-slate-700">
                Cost carburant pe lună (RON)
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="ym" style={{ fontSize: 11 }} />
                  <YAxis style={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v) => [fmtRon(Number(v)), 'Cost']}
                    labelStyle={{ fontSize: 12 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="cost"
                    stroke="#0ea5e9"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {litersByVehicle.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2">
                <div className="mb-3 text-sm font-medium text-slate-700">
                  Litri carburant per mașină
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={litersByVehicle} margin={{ bottom: 34 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis
                      dataKey="plate"
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      height={72}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis style={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(v) => [`${fmtNum(Number(v))} L`, 'Litri']}
                      labelFormatter={(p) => plateLabel(String(p))}
                      labelStyle={{ fontSize: 12 }}
                    />
                    <Bar dataKey="liters" fill="#f97316" radius={[4, 4, 0, 0]} maxBarSize={64} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {consumByVehicle.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2">
                <div className="mb-3 text-sm font-medium text-slate-700">
                  Consum L/100km per mașină (roșu = peste normat +20%)
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={consumByVehicle} margin={{ bottom: 34 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis
                      dataKey="plate"
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      height={72}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis style={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(v) => [`${fmtNum(Number(v))} L/100km`, 'Consum']}
                      labelFormatter={(p) => plateLabel(String(p))}
                      labelStyle={{ fontSize: 12 }}
                    />
                    <Bar dataKey="l100" radius={[4, 4, 0, 0]} maxBarSize={64}>
                      {consumByVehicle.map((c) => (
                        <Cell key={c.plate} fill={c.peste ? '#ef4444' : '#22c55e'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <SortTh k="plate" label="Mașină" />
                  <th className="px-3 py-2">Șofer</th>
                  <SortTh k="totalCost" label="Cost total" right />
                  <th className="px-3 py-2 text-right">Carburant</th>
                  <th className="px-3 py-2 text-right">DRY</th>
                  <SortTh k="liters" label="Litri" right />
                  <SortTh k="kmDriven" label="Km" right />
                  <SortTh k="avgL100" label="L/100km" right />
                  <th className="px-3 py-2 text-right">Normat</th>
                  <SortTh k="deviationPct" label="Abatere" right />
                  <SortTh k="costPerKm" label="Cost/km" right />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r) => (
                  <tr key={r.vehicleId} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono font-medium">
                      <Placuta vehicleId={r.vehicleId} plate={r.plate} />
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      <SoferiRand r={r} poateScriePv={poateScriePv} />
                    </td>
                    <td className="px-3 py-2 text-right font-medium">{fmtRon(r.totalCost)}</td>
                    <td className="px-3 py-2 text-right">{fmtRon(r.fuelCost)}</td>
                    <td className="px-3 py-2 text-right">{fmtRon(r.dryCost)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(r.liters)}</td>
                    <td className="px-3 py-2 text-right">
                      {r.calitateKm?.faraKm ? (
                        <span
                          className="cursor-help text-amber-700"
                          title="Nicio alimentare nu are kilometraj — consumul nu se poate calcula"
                        >
                          fără km
                        </span>
                      ) : (
                        r.kmDriven || '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.calitateKm?.faraKm ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        fmtNum(r.avgL100)
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-400">{fmtNum(r.normedL100)}</td>
                    <td
                      className={`px-3 py-2 text-right ${
                        r.deviationPct != null && r.deviationPct > 20
                          ? 'font-semibold text-rose-600'
                          : ''
                      }`}
                    >
                      {r.deviationPct == null ? '—' : `${fmtNum(r.deviationPct)}%`}
                    </td>
                    <td className="px-3 py-2 text-right">{fmtRon(r.costPerKm)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

/** Șoferii unei mașini în perioadă, după PV-uri, plus partea nealocată. */
function SoferiRand({ r, poateScriePv }: { r: VehicleReportRow; poateScriePv: boolean }) {
  const soferi = r.soferi ?? []
  return (
    <div className="space-y-0.5">
      {soferi.length === 0 && !r.nealocat?.nrTx && <span>—</span>}
      {soferi.map((s) => (
        <div key={String(s.userId)} className="whitespace-nowrap">
          {s.nume}
          {soferi.length + (r.nealocat?.nrTx ? 1 : 0) > 1 && (
            <span className="ml-1 text-xs text-slate-400">{fmtRon(s.cost)}</span>
          )}
        </div>
      ))}
      {(r.nealocat?.nrTx ?? 0) > 0 && (
        <div className="whitespace-nowrap text-xs text-amber-700">
          nealocat {fmtRon(r.nealocat!.cost)}
          {poateScriePv && (
            <>
              {' · '}
              <Link to={`/operational/flota/masini/${r.vehicleId}`} className="underline">
                de completat
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function TranzactiiTab() {
  const [plate, setPlate] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [kind, setKind] = useState<'' | 'FUEL' | 'DRY'>('')
  const [doarNealocate, setDoarNealocate] = useState(false)
  const q = useQuery({
    queryKey: ['rompetrol', 'tx', plate, from, to, kind, doarNealocate],
    queryFn: async () =>
      (
        await api.get<{ total: number; rows: TxRow[] }>('/rompetrol/transactions', {
          params: {
            plate: plate || undefined,
            from: from || undefined,
            to: to ? `${to}T23:59:59` : undefined,
            kind: kind || undefined,
            sofer: doarNealocate ? 'nealocat' : undefined,
            limit: 1000,
          },
        })
      ).data,
  })
  const vehicles = useQuery({
    queryKey: ['rompetrol', 'vehicles'],
    queryFn: async () => (await api.get<{ rows: VehicleFull[] }>('/rompetrol/vehicles')).data.rows,
  })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <PeriodFilter from={from} to={to} setFrom={setFrom} setTo={setTo} />
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase text-slate-400">Tip</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as '' | 'FUEL' | 'DRY')}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">Toate</option>
            <option value="FUEL">Carburant</option>
            <option value="DRY">DRY</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase text-slate-400">Mașină</label>
          <select
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">Toate</option>
            {(vehicles.data ?? [])
              .filter((v) => v.active)
              .map((v) => (
                <option key={v.id} value={v.plate}>
                  {v.plate}
                </option>
              ))}
          </select>
        </div>
        <label className="flex items-center gap-2 pb-1 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={doarNealocate}
            onChange={(e) => setDoarNealocate(e.target.checked)}
          />
          Doar nealocate (fără PV)
        </label>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Data</th>
              <th className="px-3 py-2">Mașină</th>
              <th className="px-3 py-2">Șofer (după PV)</th>
              <th className="px-3 py-2">Tip</th>
              <th className="px-3 py-2">Produs</th>
              <th className="px-3 py-2 text-right">Cant.</th>
              <th className="px-3 py-2 text-right">Valoare</th>
              <th className="px-3 py-2 text-right">Km bord</th>
              <th className="px-3 py-2">Factură</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(q.data?.rows ?? []).map((t) => (
              <tr key={t.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(t.txAt)}</td>
                <td className="px-3 py-2 font-mono font-medium">{t.vehicle.plate}</td>
                <td className="px-3 py-2">
                  <SoferTx t={t} />
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${t.kind === 'FUEL' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}
                  >
                    {t.kind}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-600">{t.productName ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  {fmtNum(Number(t.quantity))} {t.unit}
                </td>
                <td className="px-3 py-2 text-right font-medium">{fmtRon(Number(t.amount))}</td>
                <td className="px-3 py-2 text-right">{t.odometerKm ?? '—'}</td>
                <td className="px-3 py-2 text-slate-500">{t.invoiceNo ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-400">Total: {q.data?.total ?? 0} tranzacții</div>
    </div>
  )
}

function SoferTx({ t }: { t: TxRow }) {
  const poateScriePv = useAuthStore((s) => s.permissions.includes('operational.flota_pv:write'))
  const a = t.atribuire
  if (!a) return <span className="text-slate-400">—</span>
  if (a.fel !== 'nealocat') return <span className="text-slate-600">{a.nume}</span>
  return (
    <span className="whitespace-nowrap text-xs text-amber-700">
      {a.motiv === 'neconcordant' ? 'neclar (PV-uri care nu se leagă)' : 'nealocat'}
      {poateScriePv && (
        <>
          {' · '}
          <Link to={linkPvNou({ vehicleId: t.vehicleId, la: t.txAt })} className="underline">
            completează PV
          </Link>
        </>
      )}
    </span>
  )
}

function ImportTab() {
  const qc = useQueryClient()
  const [reportType, setReportType] = useState<'transactions_fuel' | 'transactions_dry'>(
    'transactions_fuel',
  )
  const fileRef = useRef<HTMLInputElement>(null)
  const [msg, setMsg] = useState<string | null>(null)

  const imports = useQuery({
    queryKey: ['rompetrol', 'imports'],
    queryFn: async () => (await api.get<{ rows: ImportRow[] }>('/rompetrol/imports')).data.rows,
  })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('reportType', reportType)
      return (
        await api.post('/rompetrol/import', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      ).data
    },
    onSuccess: (data: { rowsInserted: number; rowsDuplicate: number; rowsError: number }) => {
      setMsg(
        `Import OK: ${data.rowsInserted} inserate, ${data.rowsDuplicate} duplicate, ${data.rowsError} erori.`,
      )
      if (fileRef.current) fileRef.current.value = ''
      qc.invalidateQueries({ queryKey: ['rompetrol'] })
    },
    onError: () => setMsg('Eroare la import. Verifică fișierul și tipul raportului.'),
  })

  const scan = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          reportsFound: number
          rowsInserted: number
          duplicateImports: number
          errors: number
          diagnostics: string[]
        }>('/rompetrol/scan-inbox')
      ).data,
    onSuccess: (d) => {
      setMsg(
        `Scanare e-mail: ${d.reportsFound} rapoarte noi (${d.rowsInserted} tranzacții), ${d.duplicateImports} deja procesate, ${d.errors} erori.` +
          (d.diagnostics.length ? ` — ${d.diagnostics.join('; ')}` : ''),
      )
      qc.invalidateQueries({ queryKey: ['rompetrol'] })
    },
    onError: () => setMsg('Eroare la scanarea inbox-ului (verifică IMAP).'),
  })

  const scrape = useMutation({
    mutationFn: async (sinceDate?: string) =>
      (await api.post('/rompetrol/scrape', sinceDate ? { sinceDate } : {})).data,
    onSuccess: () =>
      setMsg(
        'Scraper Fill&Go pornit în fundal — se loghează, exportă rapoartele și le trimite pe email la contabilitate@. ' +
          'Datele apar după ce sosesc mailurile + scanul de inbox (sau apasă „Sincronizează din email").',
      ),
    onError: () =>
      setMsg('Eroare la pornirea scraperului (verifică ROMPETROL_SCRAPE_ENABLED + credențiale).'),
  })

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-1 font-medium">Scraper automat Fill&amp;Go</div>
        <div className="mb-3 text-sm text-slate-500">
          Se loghează singur pe manager.fillandgo.ro, exportă rapoartele (carburant + DRY) și le
          trimite pe email la contabilitate@. Rulează zilnic automat; aici îl poți porni manual.
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => scrape.mutate('2026-01-01')}
            disabled={scrape.isPending}
            className="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
          >
            Preia rapoartele din ianuarie 2026
          </button>
          <button
            onClick={() => scrape.mutate(undefined)}
            disabled={scrape.isPending}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Preia rapoartele lunii curente
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-1 font-medium">Sincronizare din email (contabilitate@)</div>
        <div className="mb-3 text-sm text-slate-500">
          Scanează inbox-ul și ingerează rapoartele Fill&amp;Go primite ca atașament Excel.
        </div>
        <button
          onClick={() => scan.mutate()}
          disabled={scan.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {scan.isPending ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Sincronizează din email
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 font-medium">Încărcare manuală raport Fill&amp;Go (Excel)</div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value as typeof reportType)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
          >
            <option value="transactions_fuel">
              Tranzacții carburant (Facturi pe departamente)
            </option>
            <option value="transactions_dry">Tranzacții DRY (Facturi DRY pe departamente)</option>
          </select>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="text-sm" />
          <button
            onClick={() => fileRef.current?.files?.[0] && upload.mutate(fileRef.current.files[0])}
            disabled={upload.isPending}
            className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
          >
            {upload.isPending ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Încarcă
          </button>
        </div>
        {msg && <div className="mt-3 text-sm text-slate-600">{msg}</div>}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Data</th>
              <th className="px-3 py-2">Sursă</th>
              <th className="px-3 py-2">Tip</th>
              <th className="px-3 py-2">Fișier</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Inserate</th>
              <th className="px-3 py-2 text-right">Duplicate</th>
              <th className="px-3 py-2 text-right">Erori</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(imports.data ?? []).map((im) => (
              <tr key={im.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                  {fmtDate(im.createdAt)}
                </td>
                <td className="px-3 py-2">{im.source}</td>
                <td className="px-3 py-2 text-slate-600">{im.reportType}</td>
                <td className="px-3 py-2 text-slate-600">{im.fileName}</td>
                <td className="px-3 py-2">{im.status}</td>
                <td className="px-3 py-2 text-right">{im.rowsTotal}</td>
                <td className="px-3 py-2 text-right text-emerald-600">{im.rowsInserted}</td>
                <td className="px-3 py-2 text-right text-slate-400">{im.rowsDuplicate}</td>
                <td className="px-3 py-2 text-right text-rose-500">{im.rowsError}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MasiniTab() {
  const vehicles = useQuery({
    queryKey: ['rompetrol', 'vehicles'],
    queryFn: async () => (await api.get<{ rows: VehicleFull[] }>('/rompetrol/vehicles')).data.rows,
  })
  const rows = vehicles.data ?? []

  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-500">
        Definește consumul normat (L/100km) și regimul TVA pentru fiecare mașină. Cine a avut mașina
        și când se ține din procesele-verbale de predare; numele scris aici e doar informativ și se
        folosește la costurile agenților numai pentru alimentările fără PV.
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Mașină</th>
              <th className="px-3 py-2">Marcă</th>
              <th className="px-3 py-2">Model</th>
              <th className="px-3 py-2">Serie șasiu</th>
              <th className="px-3 py-2">Șofer trecut (informativ)</th>
              <th className="px-3 py-2">Consum normat L/100km</th>
              <th className="px-3 py-2">Regim TVA</th>
              <th
                className="px-3 py-2"
                title="Ultima zi valabilă. Alerta sună cu 30 de zile înainte."
              >
                Expiră ITP / RCA / rovinietă
              </th>
              <th className="px-3 py-2 text-center">Activ</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((v) => (
              <VehicleRow key={v.id} v={v} />
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && !vehicles.isLoading && (
        <div className="text-sm text-slate-500">
          Nicio mașină încă — apar automat după primul import.
        </div>
      )}
      <AdaugaMasina />
    </div>
  )
}

/**
 * Adăugare manuală de mașină.
 *
 * Importul creează mașinile din alimentări, deci una abia intrată în flotă, care n-a pus
 * încă benzină, nu apare nicăieri. Fără asta nu poate fi introdusă până la prima plină.
 */
function AdaugaMasina() {
  const qc = useQueryClient()
  const [deschis, setDeschis] = useState(false)
  const [plate, setPlate] = useState('')
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [chassis, setChassis] = useState('')
  const [driver, setDriver] = useState('')

  const adauga = useMutation({
    mutationFn: async () =>
      api.post('/rompetrol/vehicles', {
        plate,
        brand: brand.trim() || null,
        model: model.trim() || null,
        chassis: chassis.trim().toUpperCase() || null,
        driverName: driver.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rompetrol'] })
      setPlate('')
      setBrand('')
      setModel('')
      setChassis('')
      setDriver('')
      setDeschis(false)
      toast.success('Mașină adăugată')
    },
    onError: (err: unknown) =>
      toast.error(
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Nu am putut adăuga mașina',
      ),
  })

  const inp = 'rounded-lg border border-slate-300 px-2 py-1 text-sm'
  if (!deschis) {
    return (
      <button
        onClick={() => setDeschis(true)}
        className="text-sm font-medium text-b4l-blue hover:underline"
      >
        + Adaugă mașină
      </button>
    )
  }
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-col gap-1">
        <label className="text-[11px] uppercase text-slate-400">Mașină</label>
        <input
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          placeholder="B750MTR"
          className={`w-28 font-mono ${inp}`}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[11px] uppercase text-slate-400">Marcă</label>
        <input value={brand} onChange={(e) => setBrand(e.target.value)} className={`w-32 ${inp}`} />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[11px] uppercase text-slate-400">Model</label>
        <input value={model} onChange={(e) => setModel(e.target.value)} className={`w-36 ${inp}`} />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[11px] uppercase text-slate-400">Serie șasiu</label>
        <input
          value={chassis}
          onChange={(e) => setChassis(e.target.value)}
          className={`w-52 font-mono text-xs ${inp}`}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[11px] uppercase text-slate-400">Șofer</label>
        <input
          value={driver}
          onChange={(e) => setDriver(e.target.value)}
          className={`w-44 ${inp}`}
        />
      </div>
      <button
        onClick={() => adauga.mutate()}
        disabled={plate.trim().length < 3 || adauga.isPending}
        className="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-40"
      >
        {adauga.isPending ? '…' : 'Adaugă'}
      </button>
      <button onClick={() => setDeschis(false)} className="px-2 py-1.5 text-sm text-slate-500">
        Renunță
      </button>
    </div>
  )
}

function VehicleRow({ v }: { v: VehicleFull }) {
  const qc = useQueryClient()
  const [brand, setBrand] = useState(v.brand ?? '')
  const [model, setModel] = useState(v.model ?? '')
  const [chassis, setChassis] = useState(v.chassis ?? '')
  const [driver, setDriver] = useState(v.driverName ?? '')
  const [normed, setNormed] = useState(v.normedL100 ?? '')
  const [regime, setRegime] = useState<'v50' | 'v100'>(v.vatRegime)
  const [active, setActive] = useState(v.active)
  const zi = (d: string | null | undefined) => (d ? d.slice(0, 10) : '')
  const [itp, setItp] = useState(zi(v.itpExpira))
  const [rca, setRca] = useState(zi(v.rcaExpira))
  const [rovinieta, setRovinieta] = useState(zi(v.rovinietaExpira))

  const dirty =
    itp !== zi(v.itpExpira) ||
    rca !== zi(v.rcaExpira) ||
    rovinieta !== zi(v.rovinietaExpira) ||
    brand !== (v.brand ?? '') ||
    model !== (v.model ?? '') ||
    chassis !== (v.chassis ?? '') ||
    driver !== (v.driverName ?? '') ||
    normed !== (v.normedL100 ?? '') ||
    regime !== v.vatRegime ||
    active !== v.active

  const save = useMutation({
    mutationFn: async () =>
      api.patch(`/rompetrol/vehicles/${v.id}`, {
        brand: brand.trim() || null,
        model: model.trim() || null,
        chassis: chassis.trim().toUpperCase() || null,
        driverName: driver.trim() || null,
        normedL100: normed === '' ? null : Number(normed),
        vatRegime: regime,
        active,
        itpExpira: itp || null,
        rcaExpira: rca || null,
        rovinietaExpira: rovinieta || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rompetrol'] }),
  })

  const inp = 'rounded-lg border border-slate-300 px-2 py-1 text-sm'
  return (
    <tr className={`hover:bg-slate-50 ${!active ? 'opacity-50' : ''}`}>
      <td className="px-3 py-2 font-mono font-medium">{v.plate}</td>
      <td className="px-3 py-2">
        <input
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          placeholder="ex. Dacia"
          className={`w-28 ${inp}`}
        />
      </td>
      <td className="px-3 py-2">
        <input
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="ex. Duster"
          className={`w-32 ${inp}`}
        />
      </td>
      <td className="px-3 py-2">
        <input
          value={chassis}
          onChange={(e) => setChassis(e.target.value)}
          placeholder="ex. TMBAJ8NX2TM015599"
          className={`w-52 font-mono text-xs ${inp}`}
        />
      </td>
      <td className="px-3 py-2">
        <input
          value={driver}
          onChange={(e) => setDriver(e.target.value)}
          placeholder="Nume șofer…"
          className={`w-44 ${inp}`}
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="number"
          step="0.1"
          value={normed}
          onChange={(e) => setNormed(e.target.value)}
          placeholder="ex. 6.5"
          className={`w-28 ${inp}`}
        />
      </td>
      <td className="px-3 py-2">
        <select
          value={regime}
          onChange={(e) => setRegime(e.target.value as 'v50' | 'v100')}
          className={inp}
        >
          <option value="v50">50% (autoturism)</option>
          <option value="v100">100% (utilitar/uz exclusiv)</option>
        </select>
      </td>
      <td className="space-y-1 px-3 py-2">
        {(
          [
            ['ITP', itp, setItp],
            ['RCA', rca, setRca],
            ['Rovinietă', rovinieta, setRovinieta],
          ] as const
        ).map(([et, val, setVal]) => (
          <label key={et} className="flex items-center gap-1 text-xs text-slate-500">
            <span className="w-16">{et}</span>
            <input
              type="date"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className={`${inp} ${val && val < new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10) ? 'border-amber-400 bg-amber-50' : ''}`}
            />
          </label>
        ))}
      </td>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4 accent-orange-500"
          title="Activ"
        />
      </td>
      <td className="px-3 py-2">
        <button
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="rounded-lg bg-orange-500 px-3 py-1 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-40"
        >
          {save.isPending ? '…' : save.isSuccess && !dirty ? '✓' : 'Salvează'}
        </button>
      </td>
    </tr>
  )
}
